import React, { useRef, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Box, Typography } from '@mui/material';
import { generateMqttChartOptions, generateMqttChartData, generateSkeletonChart } from '../../utils/mqttUtils';
import PropTypes from 'prop-types';
import zoomPlugin from 'chartjs-plugin-zoom';
import { sensorTypeLabels } from '../../utils/constants';

// Chart.js time scale and date adapter imports
import {
    Chart as ChartJS,
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin
);

function MqttChart({ 
    sensorType, 
    historicalData,
    thresholds,
    selectedTimeRange = 'infinity'
}) {
    const chartRef = useRef(null);
    
    // Debug: Log when chart re-renders
    // Debug: Track re-renders (comment out to reduce noise)
    // console.log(`MqttChart re-rendering for ${sensorType}:`, {
    //     historicalDataLength: historicalData?.length,
    //     thresholds,
    //     selectedTimeRange
    // });

    const{chartData, chartOptions} = useMemo(() => {
            const safeHistoricalData = historicalData ?? [];
            if(!safeHistoricalData || safeHistoricalData?.length === 0) {
                const { skeletonData, skeletonOptions } = generateSkeletonChart(sensorType);
                return { chartData: skeletonData, chartOptions: skeletonOptions };
            }

            const dynamicOptions = generateMqttChartOptions(
                `${sensorType} Sensor Data`, 
                sensorType
            );

            // Set y-axis range based on thresholds:
            const thresholdLines = {
                datasets: []
            }

            const safeThresholds = thresholds || { high: null, low: null };

            if (safeThresholds?.high !== null && safeThresholds?.high !== undefined && typeof safeThresholds?.high === 'number') {
                thresholdLines.datasets.push({
                    label: 'High Threshold',
                    data: historicalData.map(() => safeThresholds?.high),
                    borderColor: 'rgba(255, 0, 0, 0.5)',
                    borderDash: [5, 5],
                    pointRadius: 0
                })
            }

            if (safeThresholds?.low !== null && safeThresholds?.low !== undefined && typeof safeThresholds?.low === 'number') {
                thresholdLines.datasets.push({
                    label: 'Low Threshold',
                    data: historicalData.map(() => safeThresholds?.low),
                    borderColor: 'rgba(0, 0, 255, 0.5)',
                    borderDash: [5, 5],
                    pointRadius: 0
                    
                })
            }
            let timeUnit = 'day'; // Default
            let displayFormat = 'MM/dd'; // Default format

            switch (selectedTimeRange) {
                case '8h':
                    timeUnit = 'hour';
                    displayFormat = 'h:mm a'; // e.g., "12:00 PM"
                    break;
                case '1d':
                    timeUnit = 'hour';
                    displayFormat = 'h:mm a'; // e.g., "12:00 PM"
                    break;
                case '1w':
                    timeUnit = 'day';
                    displayFormat = 'MM/dd'; // e.g., "05/17"
                    break;
                case '2w':
                    timeUnit = 'day';
                    displayFormat = 'MM/dd'; // e.g., "05/17"
                    break;
                default:
                    timeUnit = 'day';
                    displayFormat = 'MM/dd'; // Default
            }
            

            // Intelligent time range setting
            dynamicOptions.scales.x = {
                type: 'time',
                display: true,
                time: {
                    unit: timeUnit,
                    displayFormats: {
                        day: displayFormat
                    }
                },
                ticks: {
                    color: 'white'
                },
                grid: {
                    color: 'rgba(255,255,255,0.1)'
                }
            };

            // Advanced zoom configuration for v10.5.0
            dynamicOptions.plugins = {
                ...dynamicOptions.plugins,
                zoom: {
                    zoom: {
                        wheel: {
                            enabled: window.innerWidth > 1024 ? true : false,
                            speed: 0.1,
                        },
                        pinch: {
                            enabled: window.innerWidth > 1024 ? true : false,
                        },
                        mode: 'x',
                    },
                    pan: {
                        enabled: window.innerWidth > 1024 ? true : false,
                        mode: 'x',
                    },
                    limits: {
                        x: {
                            min: 'original',
                            max: 'original'
                        }
                    }
                }
            };
            
            const generateChartData = {
                ...generateMqttChartData(
                    sensorType, 
                    safeHistoricalData, 
                    'transparent',
                    sensorType === 'Humidity' ? 'lightgreen' : sensorTypeLabels[sensorType.toLowerCase()]["color"],
                    sensorType === 'Temperature' ? 'lightsalmon' : sensorTypeLabels[sensorType.toLowerCase()]["color"]
                ),
                datasets: [
                    ...generateMqttChartData(
                        sensorType, 
                        safeHistoricalData, 
                        'transparent',
                        sensorType === 'Humidity' ? 'lightgreen' : sensorTypeLabels[sensorType.toLowerCase()]["color"],
                        sensorType === 'Temperature' ? 'lightsalmon' : sensorTypeLabels[sensorType.toLowerCase()]["color"]
                    ).datasets,
                    ...thresholdLines.datasets
                ]
            };
            
            return { chartData: generateChartData, chartOptions: dynamicOptions };
    }, [sensorType, historicalData, thresholds, selectedTimeRange]);

    return (
        <Box 
            className="mqtt-chart-box"
            sx={{ 
                position: 'relative',
                margin: 'auto'
            }}
        >
            <Typography variant="h6" color="white" textAlign="center">
                {sensorType} Sensor
            </Typography>
            {
                chartData && chartOptions ? (
                    <Typography variant="body1" color="gray" textAlign="center">
                    </Typography>
                ) : (
                    <Typography variant="body1" color="gray" textAlign="center">
                        No data available for {sensorType} sensor.
                    </Typography>
                )}
            <Line 
                ref={chartRef}
                data={chartData || {labels: [], datasets: []}} 
                options={{
                    ...(chartOptions || {}),
                    plugins: {
                        ...(chartOptions?.plugins || {}),
                        zoom: {
                            zoom: {
                                wheel: {
                                    enabled: window.innerWidth > 1024,
                                    speed: 0.1,
                                },
                                pinch: {
                                    enabled: window.innerWidth > 1024,
                                },
                                mode: 'x',
                            },
                            pan: {
                                enabled: window.innerWidth > 1024,
                                mode: 'x',
                            },
                            limits: {
                                x: {
                                    min: 'original',
                                    max: 'original'
                                },
                                y: {
                                    min: 'original',
                                    max: 'original'
                                }
                            }
                        }
                    },
                    animation: {
                        duration: 0
                    },
                    elements: {
                        point: {
                            radius: historicalData?.length > 1000 ? 0 : (window.innerWidth > 1024 ? 3 : 1)
                        }
                    },
                    interaction: {
                        mode: window.innerWidth > 1024 ? 'nearest' : 'index',
                        intersect: false,
                    },
                    responsive: true,
                    maintainAspectRatio: false
                }} 
            />
        </Box>
    );
}

MqttChart.propTypes = {
    sensorType: PropTypes.string.isRequired,
    historicalData: PropTypes.arrayOf(PropTypes.object),
    thresholds: PropTypes.shape({
        high: PropTypes.number,
        low: PropTypes.number
    }),
    selectedTimeRange: PropTypes.string
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(MqttChart, (prevProps, nextProps) => {
    // Only re-render if these specific props change
    return (
        prevProps.sensorType === nextProps.sensorType &&
        prevProps.selectedTimeRange === nextProps.selectedTimeRange &&
        prevProps.thresholds?.high === nextProps.thresholds?.high &&
        prevProps.thresholds?.low === nextProps.thresholds?.low &&
        JSON.stringify(prevProps.historicalData) === JSON.stringify(nextProps.historicalData)
    );
});